import { createAction, handleActions } from "redux-actions";
import { takeLatest } from "redux-saga/effects";
import createRequestSaga, {
  createRequestActionTypes,
} from "./lib/createRequestSaga";
import * as termTemplatesAPI from "../api/termTemplates";
import produce from "immer";
import {
  termTemplatesSuccessProps,
  CommonProps,
  GetPayload,
} from "../types/commons";
import {
  termTemplatesIdProps,
  TermTemplatesState,
  PostTermTemplatesProps,
} from "../types/termTemplates";

/* 약관 등록, 수정, 상제 부분 */
const INITIALIZE = "termTemplates/INITIALIZE";
const CHANGE_FINELD = "termTemplates/CHANGE_FINELD"; //데이터 변경용
const CHANGE_RESULT = "termTemplates/CHANGE_RESULT"; //결과값 변경

//리덕스 모듈에서 API를 사용할 수 있게 추가
const [TERM_TYPE, TERM_TYPE_SUCCESS, TERM_TYPE_FAILURE] =
  createRequestActionTypes("termTemplates/TERM_TYPE");
const [TERM_CREATE, TERM_CREATE_SUCCESS, TERM_CREATE_FAILURE] =
  createRequestActionTypes("termTemplates/TERM_CREATE");
const [TERM_DELETE, TERM_DELETE_SUCCESS, TERM_DELETE_FAILURE] =
  createRequestActionTypes("termTemplates/TERM_DELETE");
const [TERM_UPDATE, TERM_UPDATE_SUCCESS, TERM_UPDATE_FAILURE] =
  createRequestActionTypes("termTemplates/TERM_UPDATE");
const [TERM_SELECT, TERM_SELECT_SUCCESS, TERM_SELECT_FAILURE] =
  createRequestActionTypes("termTemplates/TERM_SELECT");

/* action */
export const initialize = createAction(INITIALIZE);
export const changeField = createAction(
  CHANGE_FINELD,
  ({ key, value }: CommonProps) => ({
    key, // blackList 내부 > name, blackId 등
    value, // 실제 바꾸려는 값
  }),
);

export const changeResult = createAction(
  CHANGE_RESULT,
  ({ key, value }: CommonProps) => ({
    key, // blackList success, error 변경
    value, // 실제 바꾸려는 값
  }),
);

// 블랙리스트 객실 타입 목록
export const listTermTemplatesAction = createAction(TERM_TYPE);

// 블랙리스트 객실 조회 정보
export const selectTermTemplatesAction = createAction(
  TERM_SELECT,
  ({ id }: termTemplatesIdProps) => ({
    id,
  }),
);

// 블랙리스트 객실 등록 시도
export const createTermTemplatesAction = createAction(
  TERM_CREATE,
  ({ title, content, language, is_published }: PostTermTemplatesProps) => ({
    title,
    content,
    language,
    is_published: is_published ?? false,
  }),
);

// 블랙리스트 객실 수정 시도
export const updateTermTemplatesAction = createAction(
  TERM_UPDATE,
  ({ id, title, content, language, is_published }: PostTermTemplatesProps) => ({
    id,
    title,
    content,
    language,
    is_published,
  }),
);

// 블랙리스트 객실 삭제 시도
export const deleteTermTemplatesAction = createAction(
  TERM_DELETE,
  ({ id }: termTemplatesIdProps) => ({
    id,
  }),
);

// 사가 생성
const listTermTemplatesTypeSaga = createRequestSaga(
  TERM_TYPE,
  termTemplatesAPI.listTermTemplate,
);
const createTermTemplatesSaga = createRequestSaga(
  TERM_CREATE,
  termTemplatesAPI.createTermTemplate,
);
const deleteTermTemplatesSaga = createRequestSaga(
  TERM_DELETE,
  termTemplatesAPI.deleteTermTemplate,
);
const updateTermTemplatesSaga = createRequestSaga(
  TERM_UPDATE,
  termTemplatesAPI.updateTermTemplate,
);
const selectTermTemplatesSaga = createRequestSaga(
  TERM_SELECT,
  termTemplatesAPI.selectTermTemplates,
);

export function* termTemplatesSaga() {
  yield takeLatest(TERM_TYPE, listTermTemplatesTypeSaga);
  yield takeLatest(TERM_CREATE, createTermTemplatesSaga);
  yield takeLatest(TERM_DELETE, deleteTermTemplatesSaga);
  yield takeLatest(TERM_UPDATE, updateTermTemplatesSaga);
  yield takeLatest(TERM_SELECT, selectTermTemplatesSaga);
}

//initialState
const initialState: TermTemplatesState = {
  termTemplates: {
    id: 0,
    hotel_id: "",
    title: "",
    content: "",
    language: "ko",
    is_published: false,
    createdAt: null,
    updatedAt: null,
  },
  termTemplatesCreateSuccess: false, //등록 성공 관련
  termTemplatesUpdateSuccess: false, //수정 성공 관련
  termTemplatesDeleteSuccess: false, //삭제 성공 관련
  termTemplatesCreateError: null,
  termTemplatesUpdateError: null,
  termTemplatesDeleteError: null,
};

// 페이로드를 가져오기 위한 헬퍼 타입
type Payloads = GetPayload<
  | typeof initialize
  | typeof changeField
  | typeof changeResult
  | typeof listTermTemplatesAction
  | typeof selectTermTemplatesAction
  | typeof createTermTemplatesAction
  | typeof updateTermTemplatesAction
  | typeof deleteTermTemplatesAction
>;

const termTemplates = handleActions<TermTemplatesState, Payloads>(
  {
    [INITIALIZE]: (state) => initialState, // initialState를 넣으면 초기 상태로 바뀜
    [CHANGE_FINELD]: (state, { payload: { key, value } }) =>
      produce(state, (draft) => {
        if (key === "id") draft.termTemplates.id = value;
        if (key === "title") draft.termTemplates.title = value as string;
        if (key === "content") draft.termTemplates.content = value as string;
        if (key === "language") draft.termTemplates.language = value as string;
        if (key === "is_published")
          draft.termTemplates.is_published = value as boolean;
      }),
    [CHANGE_RESULT]: (
      state,
      { payload: { key, value } }: termTemplatesSuccessProps,
    ) =>
      produce(state, (draft) => {
        // @ts-ignore: key가 string으로 들어와서 타입 추론이 어려울 때 예외 처리
        draft[key] = value;
      }),
    // 약관 조회 성공
    [TERM_TYPE_SUCCESS]: (state) => ({
      ...state,
      termTemplatesCreateError: null,
    }),
    // 약관 조회 실패
    [TERM_TYPE_FAILURE]: (state, { payload: error }) => ({
      ...state,
      termTemplatesCreateError: error,
    }),
    // 약관 등록 화면 조회 성공
    [TERM_CREATE_SUCCESS]: (state, { payload: termTemplates }) => ({
      ...state,
      termTemplates: termTemplates,
      termTemplatesCreateSuccess: true,
      termTemplatesCreateError: null,
    }),
    // 약관 등록 화면 조회 실패
    [TERM_CREATE_FAILURE]: (state, { payload: error }) => ({
      ...state,
      termTemplatesCreateError: error,
    }),
    // 약관 삭제 성공
    [TERM_DELETE_SUCCESS]: (state, { payload: termTemplates }) => ({
      ...state,
      termTemplatesDeleteSuccess: true,
      termTemplatesDeleteError: null,
    }),
    // 약관 삭제 실패
    [TERM_DELETE_FAILURE]: (state, { payload: error }) => ({
      ...state,
      termTemplatesDeleteError: error,
    }),
    // 약관 수정 성공
    [TERM_UPDATE_SUCCESS]: (state, { payload: termTemplates }) => ({
      ...state,
      termTemplatesUpdateSuccess: true,
      termTemplatesUpdateError: null,
    }),
    // 약관 수정 실패
    [TERM_UPDATE_FAILURE]: (state, { payload: error }) => ({
      ...state,
      termTemplatesUpdateError: error,
    }),
    // 약관 검색 조회 성공
    [TERM_SELECT_SUCCESS]: (state, { payload: termTemplates }) => ({
      ...state,
      termTemplates: {
        id: termTemplates.id,
        hotel_id: termTemplates.hotel_id,
        title: termTemplates.title,
        content: termTemplates.content,
        language: termTemplates.language,
        is_published: termTemplates.is_published,
        createdAt: termTemplates.created_at,
        updatedAt: termTemplates.updated_at,
      },
    }),
    // 약관 검색 조회 실패
    [TERM_SELECT_FAILURE]: (state, { payload: error }) => ({
      ...state,
      termTemplatesUpdateError: error,
    }),
  },
  initialState,
);

export default termTemplates;
